import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../../shared/ConfirmDialog';
import { useOptionalToast } from '../../../contexts/ToastContext';
import { useFlag } from '../../../hooks/useFeatureFlags';
import { MODEL_CATALOG_REVIEW_KEY, useModelCatalogReview } from '../../../hooks/useModelCatalogReview';
import { useCreateModel, useModelProviders, useUpdateModel } from '../../../hooks/useModels';
import { refusalReason } from '../../../utils/apiRefusal';
import type { CreateModelInput, ModelReviewAvailable, ModelReviewEntry, ModelReviewField, ModelStatus } from '../../../types';
import { ReviewSourceStrip } from './ReviewSourceStrip';
import { ReviewFilterBar, type ReviewFilter } from './ReviewFilterBar';
import { ReviewCatalogTable } from './ReviewCatalogTable';
import { ReviewAvailableTable, availableKey } from './ReviewAvailableTable';
import {
  FIELD_LABELS,
  PRICE_FIELDS,
  formatFieldValue,
  isModelsDevUnavailable,
  patchFor,
  providerValueFor,
} from './reviewUtils';

/**
 * The Model Catalog Review tab (task 1093): source strip, filter counts, the
 * catalog's Model rows against the provider values, then the provider models
 * the catalog lacks. The tab itself is gated on `innovate.fe.models.review`
 * (by the page); the four in-place actions additionally need
 * `innovate.fe.model-catalog.manage`, the frontend mirror of the service's
 * `innovate.be.model-catalog.manage` that gates `POST`/`PATCH /models`.
 */

type MarkStatus = Extract<ModelStatus, 'deprecated' | 'retired'>;

type Pending =
  | { kind: 'use'; entry: ModelReviewEntry; fields: ModelReviewField[] }
  | { kind: 'mark'; entry: ModelReviewEntry; status: MarkStatus };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Narrow the review to one provider or one Model (task 1167): the Models page's
 * "Review this provider", "Open in Review" and "differs" marks land here. The
 * counts and both tables read the narrowed rows; absent, the whole catalog.
 */
export interface ReviewScope {
  providerKey?: string | null;
  modelRowId?: string | null;
}

export function ModelCatalogReviewTab({ scope }: { scope?: ReviewScope } = {}) {
  const reviewFlag = useFlag('innovate.fe.models.review');
  const manageFlag = useFlag('innovate.fe.model-catalog.manage');
  const canWrite = reviewFlag && manageFlag;
  const review = useModelCatalogReview();
  const { data: providers } = useModelProviders();
  const update = useUpdateModel();
  const create = useCreateModel();
  const queryClient = useQueryClient();
  const toast = useOptionalToast();

  const [filter, setFilter] = useState<ReviewFilter>('all');
  const [pending, setPending] = useState<Pending | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Rows added from this page keep their place in the available table after
  // the review refetches (the refetched document lists them in the catalog).
  const addedEntries = useRef(new Map<string, ModelReviewAvailable>());
  const [added, setAdded] = useState<Map<string, string>>(new Map());

  const fullDoc = review.data;
  const providerKey = scope?.providerKey ?? null;
  const modelRowId = scope?.modelRowId ?? null;
  const doc = useMemo(() => {
    if (!fullDoc || (!providerKey && !modelRowId)) return fullDoc;
    return {
      ...fullDoc,
      models: fullDoc.models.filter(
        (m) => (!modelRowId || m.id === modelRowId) && (!providerKey || m.provider === providerKey),
      ),
      // One Model's review has no "available" rows; a provider's keeps its own.
      available: modelRowId ? [] : fullDoc.available.filter((a) => !providerKey || a.provider === providerKey),
    };
  }, [fullDoc, providerKey, modelRowId]);
  const modelsDevUnavailable = doc ? isModelsDevUnavailable(doc.sources) : false;

  const providerIdByKey = useMemo(
    () => new Map((providers ?? []).map((p) => [p.key, p.id] as const)),
    [providers],
  );

  const counts = useMemo(() => {
    const models = doc?.models ?? [];
    return {
      all: models.length,
      attention: models.filter((m) => m.reviewState === 'deprecated' || m.reviewState === 'unknown').length,
      differs: models.filter((m) => m.differs.length > 0).length,
      newer: (doc?.available ?? []).filter((a) => a.newerThanCatalog).length,
    };
  }, [doc]);

  const catalogRows = useMemo(() => {
    const models = doc?.models ?? [];
    if (filter === 'attention') return models.filter((m) => m.reviewState === 'deprecated' || m.reviewState === 'unknown');
    if (filter === 'differs') return models.filter((m) => m.differs.length > 0);
    return models;
  }, [doc, filter]);

  const availableRows = useMemo(() => {
    const listed = doc?.available ?? [];
    const listedKeys = new Set(listed.map(availableKey));
    const kept = [...addedEntries.current.entries()].filter(([key]) => !listedKeys.has(key)).map(([, entry]) => entry);
    const rows = [...listed, ...kept].sort((a, b) => Date.parse(b.releasedAt ?? '0') - Date.parse(a.releasedAt ?? '0'));
    return filter === 'newer' ? rows.filter((r) => r.newerThanCatalog || added.has(availableKey(r))) : rows;
  }, [doc, filter, added]);

  const runUse = async (entry: ModelReviewEntry, fields: ModelReviewField[]) => {
    setBusyId(entry.id);
    try {
      await update.mutateAsync({ id: entry.id, data: patchFor(entry, fields) });
      const what = fields.length === 1 ? FIELD_LABELS[fields[0]] : plural(fields.length, 'value', 'values');
      toast?.success(`${what} updated for ${entry.modelId}`, entry.configCount > 0 ? `${plural(entry.configCount, 'config inherits', 'configs inherit')} the new value.` : undefined);
    } catch (error) {
      toast?.error(`Could not update ${entry.modelId}`, refusalReason(error));
    } finally {
      setBusyId(null);
    }
  };

  const runMark = async (entry: ModelReviewEntry, status: MarkStatus) => {
    setBusyId(entry.id);
    try {
      await update.mutateAsync({ id: entry.id, data: { status } });
      toast?.success(`${entry.modelId} marked ${status}`);
    } catch (error) {
      toast?.error(`Could not mark ${entry.modelId} ${status}`, refusalReason(error));
    } finally {
      setBusyId(null);
    }
  };

  const handleUse = (entry: ModelReviewEntry, fields: ModelReviewField[]) => {
    if (fields.some((f) => PRICE_FIELDS.has(f))) setPending({ kind: 'use', entry, fields });
    else void runUse(entry, fields);
  };

  const handleAdd = async (entry: ModelReviewAvailable) => {
    const providerId = providerIdByKey.get(entry.provider);
    const pricing = entry.pricing;
    if (!providerId || !pricing || entry.contextWindow === null || entry.maxOutputTokens === null) return;
    const key = availableKey(entry);
    const payload: CreateModelInput = {
      providerId,
      modelId: entry.modelId,
      displayName: entry.displayName ?? entry.modelId,
      contextWindow: entry.contextWindow,
      maxOutputTokens: entry.maxOutputTokens,
      inputPriceMTok: Number(pricing.base.inputPriceMTok),
      outputPriceMTok: Number(pricing.base.outputPriceMTok),
      ...(pricing.cache.readPriceMTok !== null ? { cacheReadPriceMTok: pricing.cache.readPriceMTok } : {}),
      ...(pricing.cache.writePriceMTok !== null ? { cacheWritePriceMTok: pricing.cache.writePriceMTok } : {}),
      ...(pricing.tiers && pricing.tiers.length > 0 ? { pricingTiers: pricing.tiers } : {}),
      ...(entry.releasedAt ? { releasedAt: entry.releasedAt } : {}),
      ...(entry.shutdownDate ? { shutdownDate: entry.shutdownDate } : {}),
      status: 'active',
    };
    setBusyKey(key);
    try {
      const model = await create.mutateAsync(payload);
      addedEntries.current.set(key, entry);
      setAdded((prev) => new Map(prev).set(key, model.id));
      queryClient.invalidateQueries({ queryKey: MODEL_CATALOG_REVIEW_KEY });
      toast?.success(`${entry.modelId} added to the catalog`, 'Add a Model Config to use it.');
    } catch (error) {
      toast?.error(`Could not add ${entry.modelId}`, refusalReason(error));
    } finally {
      setBusyKey(null);
    }
  };

  const confirm = async () => {
    if (!pending) return;
    const current = pending;
    setPending(null);
    if (current.kind === 'use') await runUse(current.entry, current.fields);
    else await runMark(current.entry, current.status);
  };

  if (review.isLoading) {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500 dark:text-gray-400">Loading the catalog review…</div>;
  }
  if (review.error || !doc) {
    return (
      <div role="alert" className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-red-500 dark:text-red-400">
        <span>Could not load the catalog review. {review.error ? refusalReason(review.error) : ''}</span>
        <button type="button" onClick={() => review.refetch()} className="text-indigo-600 hover:underline dark:text-indigo-400">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="model-catalog-review">
      <ReviewSourceStrip sources={doc.sources} onRefresh={review.refresh} refreshing={review.isRefreshing} />
      {review.refreshError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          Refresh failed: {refusalReason(review.refreshError)}
        </p>
      )}

      <ReviewFilterBar counts={counts} active={filter} onChange={setFilter} />

      <section aria-labelledby="review-catalog-heading" className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 id="review-catalog-heading" className="text-base font-medium text-gray-900 dark:text-white">
            In the catalog
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Stored value above, provider value below. Differences are highlighted
            {canWrite ? ' and carry an action that writes the Model row.' : '.'}
          </span>
        </div>
        <ReviewCatalogTable
          entries={catalogRows}
          modelsDevUnavailable={modelsDevUnavailable}
          canWrite={canWrite}
          busyId={busyId}
          onUse={handleUse}
          onMark={(entry, status) => setPending({ kind: 'mark', entry, status })}
        />
      </section>

      <section aria-labelledby="review-available-heading" className="space-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 id="review-available-heading" className="text-base font-medium text-gray-900 dark:text-white">
            Available, not in the catalog
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {plural(availableRows.length, 'model', 'models')} · newest first. Adding creates the Model row; a config is a separate step.
          </span>
        </div>
        <ReviewAvailableTable
          rows={availableRows}
          modelsDevUnavailable={modelsDevUnavailable}
          canWrite={canWrite}
          providerIdByKey={providerIdByKey}
          added={added}
          busyKey={busyKey}
          onAdd={handleAdd}
        />
      </section>

      <ConfirmDialog
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={confirm}
        title={confirmTitle(pending)}
        message={pending ? <ConfirmBody pending={pending} /> : null}
        confirmLabel={confirmLabel(pending)}
        confirmStyle={pending?.kind === 'mark' && pending.status === 'retired' ? 'danger' : 'primary'}
      />
    </div>
  );
}

function confirmTitle(pending: Pending | null): string {
  if (!pending) return '';
  if (pending.kind === 'mark') {
    return pending.status === 'retired' ? `Retire ${pending.entry.modelId}?` : `Mark ${pending.entry.modelId} deprecated?`;
  }
  return `Update pricing for ${pending.entry.modelId}?`;
}

function confirmLabel(pending: Pending | null): string {
  if (!pending) return 'Confirm';
  if (pending.kind === 'mark') return pending.status === 'retired' ? 'Mark retired' : 'Mark deprecated';
  return pending.fields.length > 1 ? `Update ${pending.fields.length} values` : 'Update price';
}

function ConfirmBody({ pending }: { pending: Pending }) {
  const { entry } = pending;
  const references = `${plural(entry.configCount, 'config', 'configs')} · ${plural(entry.agentCount, 'agent', 'agents')}`;

  if (pending.kind === 'mark' && pending.status === 'deprecated') {
    return (
      <div className="space-y-2" data-testid="confirm-mark-deprecated">
        <p>
          Model pickers will show {entry.modelId} with a deprecated warning
          {entry.shutdownDate ? ` and its shutdown date (${entry.shutdownDate})` : ''}. Model Configs on it keep working,
          and new configs are still allowed.
        </p>
        <p>You can set the status back to active on the Model's page.</p>
      </div>
    );
  }

  if (pending.kind === 'mark') {
    return (
      <div className="space-y-2" data-testid="confirm-mark-retired">
        <p className="font-medium">{references} reference it.</p>
        <p>
          Retired Models cannot be chosen for new Model Configs and are hidden from model pickers. Existing configs keep
          working until the provider refuses requests for the model.
        </p>
        <p>You can set the status back to active on the Model's page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="confirm-pricing">
      <p className="font-medium">{references} run on this model.</p>
      <ul className="space-y-0.5 text-sm">
        {pending.fields.map((field) => (
          <li key={field} data-testid={`confirm-field-${field}`}>
            {FIELD_LABELS[field]}: {formatFieldValue(field, entry.stored[field])} → {formatFieldValue(field, providerValueFor(entry, field))}
          </li>
        ))}
      </ul>
      <p>
        The new price applies to every subsequent call for every account. Past usage is not recalculated. Source:
        models.dev (advisory).
      </p>
    </div>
  );
}
